import { formatCurrency } from "src/shared/utils/formatCurrency"
import { D, DAY, Flight, fmtDay } from "./segmentEngine"

/**
 * The gate a legacy campaign sits behind until someone migrates it (MPN-1803).
 *
 * The screen would otherwise open a legacy campaign on Even and migrate it as a side effect
 * of the first Save. Migration is not reversible — a segmented campaign reads differently on
 * the old Budget screen and publishes from different policies — so it takes a deliberate act
 * rather than a save someone meant as an edit.
 *
 * It states what will happen before it happens, because the one thing an operator cannot see
 * from the old screen is which days the new windows will cover.
 */
export function CustomizeBudget({
  campaignName,
  amount,
  flight,
  today,
  busy,
  error,
  onMigrate,
  onBack,
}: {
  campaignName: string
  amount: number
  flight: Flight
  today: string
  busy?: boolean
  error?: string | null
  onMigrate: () => void
  onBack: () => void
}) {
  // The window migration will actually write: from today where the campaign is already
  // running, from its own start where it has not begun.
  const from = D(flight.start) > D(today) ? flight.start : today
  const days = Math.max(0, Math.round((D(flight.end) - D(from)) / DAY) + 1)
  const ended = D(flight.end) < D(today)

  return (
    <div className="seg-migrate">
      <button type="button" className="seg-link seg-migrate__back" onClick={onBack}>
        ‹ All campaigns
      </button>

      <div className="seg-migrate__panel">
        <h2 className="seg-migrate__title">{campaignName}</h2>
        <p className="seg-migrate__lede">
          This campaign runs on a single daily rate. Customizing its budget splits that into
          spend windows you can shape — evenly, by month, or by your own date ranges.
        </p>

        {ended ? (
          <p className="seg-migrate__blocked">
            Its dates ended {fmtDay(flight.end)}, so there are no remaining days to split.
            Extend the end date on Campaign first.
          </p>
        ) : (
          <dl className="seg-migrate__preview">
            <div>
              <dt>Starts</dt>
              <dd>{fmtDay(from)}</dd>
            </div>
            <div>
              <dt>Ends</dt>
              <dd>{fmtDay(flight.end)}</dd>
            </div>
            <div>
              <dt>Days</dt>
              <dd>{days}</dd>
            </div>
            <div>
              <dt>Amount</dt>
              <dd>{formatCurrency(amount)}</dd>
            </div>
          </dl>
        )}

        {/* Said plainly rather than in a tooltip: it is the only part of this that cannot be
            undone, and the days already spent are the part people assume will be included. */}
        <p className="seg-migrate__note">
          One Even window is created over the {days} day{days === 1 ? "" : "s"} that have not
          spent yet. Days already spent are left alone, and this cannot be undone.
        </p>

        {error && <p className="seg-migrate__error">{error}</p>}

        <button
          type="button"
          className="ds-btn ds-btn--primary"
          disabled={busy || ended}
          onClick={onMigrate}
        >
          {busy ? "Customizing…" : "Customize Budget"}
        </button>
      </div>
    </div>
  )
}
